// Remote presence (spec §6.5): cursors interpolated towards their latest
// position for smooth motion, fading out after 10 s idle, plus outlines of
// what other people have selected. Draws on the interactive layer.
import type { Peer } from '../../shared/protocol.ts';
import type { BoardObject } from '../../shared/types.ts';
import type { Store } from '../model/store.ts';

const IDLE_MS = 10_000;
const FADE_MS = 800;
const SMOOTHING = 0.35;           // fraction of the remaining distance covered per frame
const MAX_OUTLINED = 200;

interface RemotePeer extends Peer {
  shown: [number, number] | null;  // interpolated cursor position (world)
  lastMove: number;
}

export class Presence {
  /** Called when something needs drawing (interpolation, fade, new data). */
  onChange: () => void = () => {};
  /** Called when the set of peers changes (for the avatar list). */
  onPeersChange: () => void = () => {};
  private readonly peers = new Map<string, RemotePeer>();
  private fadeTimer = 0;

  get list(): Peer[] {
    return [...this.peers.values()];
  }

  /** Whether any of these objects is selected by someone else (their outlines must follow). */
  touches(ids: ReadonlySet<string>): boolean {
    for (const p of this.peers.values()) {
      if (p.selection?.some((id) => ids.has(id))) return true;
    }
    return false;
  }

  setAll(peers: readonly Peer[]): void {
    this.peers.clear();
    for (const p of peers) this.update(p, false);
    this.onPeersChange();
    this.onChange();
  }

  update(p: Peer, notify = true): void {
    const prev = this.peers.get(p.userId);
    const moved = p.cursor !== undefined && JSON.stringify(p.cursor) !== JSON.stringify(prev?.cursor);
    const shown = p.cursor ? (prev?.shown && prev.cursor ? prev.shown : [...p.cursor] as [number, number]) : null;
    this.peers.set(p.userId, { ...prev, ...p, shown, lastMove: moved || !prev ? performance.now() : prev.lastMove });
    if (!prev && notify) this.onPeersChange();
    this.scheduleFade();
    this.onChange();
  }

  remove(userId: string): void {
    if (!this.peers.delete(userId)) return;
    this.onPeersChange();
    this.onChange();
  }

  /**
   * Draws cursors and remote selections (world transform set). Returns true
   * while something is still animating, so the caller keeps redrawing.
   */
  draw(ctx: CanvasRenderingContext2D, zoom: number, store: Store, outline: (o: BoardObject, color: string) => void): boolean {
    const now = performance.now();
    let animating = false;
    for (const p of this.peers.values()) {
      if (p.selection?.length) {
        for (const id of p.selection.slice(0, MAX_OUTLINED)) {
          const o = store.get(id);
          if (o) outline(o, p.color);
        }
      }
      if (!p.cursor || !p.shown) continue;
      const alpha = 1 - Math.min(1, Math.max(0, now - p.lastMove - IDLE_MS) / FADE_MS);
      if (alpha <= 0) continue;
      if (alpha < 1) animating = true;
      const dx = p.cursor[0] - p.shown[0], dy = p.cursor[1] - p.shown[1];
      if (Math.hypot(dx, dy) * zoom > 0.5) {
        p.shown = [p.shown[0] + dx * SMOOTHING, p.shown[1] + dy * SMOOTHING];
        animating = true;
      } else {
        p.shown = [p.cursor[0], p.cursor[1]];
      }
      drawCursor(ctx, p.shown[0], p.shown[1], zoom, p.color, p.name, alpha);
    }
    return animating;
  }

  /** Wakes the renderer when the next cursor is due to start fading. */
  private scheduleFade(): void {
    clearTimeout(this.fadeTimer);
    const now = performance.now();
    let next = Infinity;
    for (const p of this.peers.values()) {
      const due = p.lastMove + IDLE_MS - now;
      if (p.cursor && due > 0) next = Math.min(next, due);
    }
    if (next < Infinity) this.fadeTimer = window.setTimeout(() => { this.onChange(); this.scheduleFade(); }, next + 16);
  }
}

function drawCursor(ctx: CanvasRenderingContext2D, x: number, y: number, zoom: number, color: string, name: string, alpha: number): void {
  const s = 1 / zoom; // draw in screen pixels
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.scale(s, s);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, 17);
  ctx.lineTo(4.5, 13);
  ctx.lineTo(8, 20.5);
  ctx.lineTo(10.8, 19.2);
  ctx.lineTo(7.4, 12);
  ctx.lineTo(13, 12);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = '#fff';
  ctx.stroke();

  ctx.font = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';
  const w = ctx.measureText(name).width + 12;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(12, 20, w, 20, 6);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, 18, 30);
  ctx.restore();
}
